import { journeyElementsStore } from 'Global/JourneyElements/JourneyElementsStore';
import useAddOns from 'Hooks/useAddOns';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import useLocationAndRoutes from 'Hooks/useLocationAndRoutes';
import usePassengerTypes from 'Hooks/usePassengerTypes';
import { usePreventBackFrom } from 'Hooks/usePreventBackFrom';
import useStore from 'Hooks/useStore';
import { CustomLocationState } from 'Models/_HumanWritten/LocationState';
import { FerryTripEntity } from 'Models/Entities';
import { appConfigStore } from 'Models/AppConfigStore';
import { ROUTES } from 'Constants';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import {
	canNavigateToCartStep,
	canNavigateToPaymentStep,
	canNavigateToPostPaymentStep,
	dataToFerryTripBookingDto,
	fetchFullBookingById,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { FerryBookingCreationDto } from 'Models/Dtos/BookingDtos';
import { onWizardSuccess } from 'Util/_HumanWritten/BookingWizard/BookingWizardUtils';
import { ferryBookingToWizardData } from 'Util/_HumanWritten/BookingWizard/FerryBookingToWizardDataUtils';
import { isNotNullOrUndefined, isNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { assignDriverDetailsToPassenger } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import { FerryFilters } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Sidebar/FerryFilters/FerryFilters';
import { checkPassengersAndDriver } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Sidebar/FerryFilters/PassengerDetails/PassengerTypeInputs';
import { CartTab } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/CartTab';
import CustomerTab from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Customer/CustomerTab';
import { PaymentTabWrap } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Payment/PaymentTabWrap';
import { PostPaymentTab } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/PostPayment/PostPaymentTab';
import { ReservationTab } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Reservation/ReservationTab';
import { TermsAndConditions } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/TermsAndConditions/TermsAndConditions';
import { useUserTypeTermsOverride } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/TermsAndConditions/useUserTypeTermsOverride';
import { TicketsTab } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Tickets/TicketsComponent';
import { usePaymentCheckOut } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import { PAYMENT_FORM_ID } from 'Views/Components/_HumanWritten/Payment/usePayment';
import { BookingWizardTabNames } from './BookingWizardConstants';
import { BookingWizardData } from './BookingWizardData/BaseBookingWizardData';
import { PassengerBookingWizardData } from './BookingWizardData/PassengerBookingWizardData';
import { VehicleBookingWizardData } from './BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from './BookingWizardDataStore';
import { fetchTripsForWizard } from './BookingWizardHelpers';
import { useWizardFetchedDataStore } from './BookingWizardState/BookingWizardFetchedData';
import { WizardErrors } from './BookingWizardTypes';
import { getDisableLinkForMultiFreight } from './ProgressBar/Utils/getDisableLinkForMultiFreight';
import { getFreightBookingTabs } from './WizardSteps/FreightWizardSteps';
import { getPassengerBookingTabs } from './WizardSteps/PassengerOnlyWizardSteps';
import { getVehicleBookingTabs } from './WizardSteps/VehicleWizardSteps';
import { useSubjectUserDisplayNameId } from './WizardSteps/BookingDetails/useSubjectUserDisplayNameId';
import { useDynamicBookingFieldsStore } from 'Stores/DynamicBookingFieldsStore';
import { FreightTypeDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { BookingWizard, BookingWizardTab, SidebarStatus, VisibilityStatus } from './BookingWizard';
import LoginStep from './WizardSteps/LoginStep/LoginStep';
import { getCenteredPageContentClass } from './WizardSteps/BulkBooking/utils';
import { getBulkTicketsSteps } from './WizardSteps/BulkTickets/BulkTicketsSteps';
import { useSeatingTabLegs } from './WizardSteps/Seating/useSeatingTab';
import { useSeatingStore } from 'Stores/SeatingStore';

export function BookingWizardWrapInner() {
	const baseUrl = '/booking-wizard';
	const { isMobileBookingWizard } = useDeviceDetect();
	const navigate = useNavigate();
	const location = useLocation();
	const store = useStore();
	const { isStaff } = store;
	const isJourneyElementsLoading = journeyElementsStore(x => x.isLoading);

	const [selectedTrips, setSelectedTrips] = useState<SelectedTrips>({});
	const [refresh, setRefresh] = useState<boolean>(false);
	const [bookingCreationFailed, setBookingCreationFailed] = useState<boolean>(false);
	const [tabIsLoading, setTabIsLoading] = useState<boolean>(false);

	const {
		wizardData,
		setWizardData,
		oldWizardData,
		setBaseWizardErrors,
		setInnerWizardErrors,
		setOldWizardData,
		termsAndConditionsTypes,
		setTermsAndConditionsTypes,
	} = useBookingWizardDataStore();

	// The desktop entry is guarded in HomeSearch, but the mobile wizard — and any bookmarked or
	// emailed `/booking-wizard?type=passenger` link — enters the wizard here directly. Redirect a
	// NEW passenger booking home when the passenger option isn't offered to this viewer
	// (showPassengerBookingOption: staff always when enabled, customers also need the customer flow).
	// Editing an existing passenger booking (bookingToEdit set) is intentionally still allowed.
	useEffect(() => {
		const isNewBooking = wizardData.bookingToEdit === '' || wizardData.bookingToEdit === undefined;
		if (wizardData.tabSelected === 'passenger'
			&& isNewBooking
			&& !appConfigStore.showPassengerBookingOption) {
			navigate('/home');
		}
	}, [wizardData.tabSelected, wizardData.bookingToEdit]);

	const {
		setDisableContinueButton,
		paymentProcessing,
		disableContinueButton,
	} = usePaymentCheckOut();

	// by storing this in a state variable, it will not be updated in the future when the user clicks the button to
	// accept the t and cs
	/**
	 * True when user has entered their payment details and pressed 'Pay now'.
	 */
	const [paymentDetailsEntered, setPaymentDetailsEntered] = useState<boolean>(false);
	const [pageLoaded, setPageLoaded] = useState<boolean>(false);
	const [acceptedTandCsStatic] = useState(wizardData.acceptedTsAndCs);

	const userTypeTerms = useUserTypeTermsOverride(wizardData.userId);

	// Booking subject's customer type (selected customer for staff-on-behalf,
	// otherwise the logged-in user). Resolved reactively so the tab definitions
	// below — whose visibility/validation can depend on customer-type-restricted
	// dynamic fields — rebuild once the lookup lands.
	const subjectUserDisplayNameId = useSubjectUserDisplayNameId();

	// Dynamic booking fields drive the visibility of the Details / freight Contact
	// tabs, so they must be loaded here — the tab components can't do it, as they
	// only mount once their tab is already visible. Subscribing (rather than
	// reading getState()) makes the tab definitions rebuild when the fields land.
	const { loaded: dynamicFieldsLoaded, loadFields: loadDynamicFields } = useDynamicBookingFieldsStore();
	useEffect(() => {
		if (!dynamicFieldsLoaded) loadDynamicFields();
	}, [dynamicFieldsLoaded, loadDynamicFields]);

	// useBookingWizardWatch({ wizardData, paymentDetailsEntered, paymentProcessing });

	const isMultiFreight = wizardData.innerWizardData.type === 'freight' && wizardData.innerWizardData.items.length > 0;
	usePreventBackFrom('/booking-wizard/cart', isMultiFreight);

	useAddOns();
	usePassengerTypes();
	// Fetched here rather than in the seating step itself: the step list is built synchronously and
	// decides the step's visibility from this data, so a step that owned its own fetch could never
	// become visible in the first place.
	//
	// Both legs, not just the departing one. The fetch also checks the booking's seats against the
	// sailing that has to honour them, and a returning seat left over from a sailing the booker
	// changed away from would otherwise reach the cart unexamined.
	useSeatingTabLegs();
	/*
		Subscribed, not read.

		The step list is built synchronously by `getPassengerBookingTabs`, and its seating step asks
		`getSeatingTabVisibility`, which reads the seating store with `getState()`. The fetch above
		re-renders this component when the query resolves — but it writes the store from an effect,
		which runs after that render. So the list was built while the store was still empty and
		nothing rebuilt it afterwards: the Seats step stayed missing until some unrelated change
		forced another render. On a one-way booking nothing else did, so adding and removing a
		passenger was the only way to make the step appear at all.
	*/
	useSeatingStore(state => state.byTripId);
	useLocationAndRoutes();

	const { freightTabData, fetchPassengerTabData, fetchVehicleTabData, fetchFreightTabData, fetchAddOnTabData } =
		useWizardFetchedDataStore();

	const freightTypeDtos = useMemo<FreightTypeDto[]>(() => {
		return Object.values(freightTabData?.freightTypes ?? {}).flat();
	}, [freightTabData]);

	// Fetch all the data for the wizard
	useEffect(() => {
		if (stringNotEmpty(wizardData.userId)) {
			fetchPassengerTabData(wizardData.userId);

			fetchAddOnTabData();

			if (wizardData.tabSelected === 'vehicle') {
				const convertedWizardData: FerryBookingCreationDto | null = isNotNullOrUndefined(wizardData)
					? dataToFerryTripBookingDto(wizardData, false, false, false)
					: null;

				if (isNullOrUndefined(convertedWizardData) || stringIsEmpty(wizardData.departureTicketId)) {
					return;
				}

				fetchVehicleTabData(convertedWizardData);
			}
		}

		if (wizardData.tabSelected === 'freight') {
			fetchFreightTabData();
		}
	}, [wizardData.userId, wizardData.tabSelected]);

	useEffect(() => {
		fetchTripsForWizard(
			wizardData.departureTicketId ?? '',
			wizardData.returningTicketId ?? '',
			selectedTrips,
			(trips: SelectedTrips) => {
				setSelectedTrips(trips);
			},
			wizardData.userId,
		);
	}, [wizardData.departureTicketId, wizardData.returningTicketId, wizardData.userId]);

	useEffect(() => {
		// Wait for journey elements (locations, routes, stops) to load before processing booking
		if (isJourneyElementsLoading) {
			return;
		}

		if (
			wizardData.bookingToEdit !== '' &&
			wizardData.bookingToEdit !== undefined &&
			// && bookingWizardData.departureTicketId === ''
			!wizardData.departingJourney
		) {
			fetchFullBookingById(wizardData.bookingToEdit, null, true).then(x => {
				const bookingAsWizardData = ferryBookingToWizardData(
					isNotNullOrUndefined(x) ? [x] : [],
					undefined,
					selectedTrips,
					true,
					wizardData.departureTrip === undefined,
				)[0];
				setOldWizardData(bookingAsWizardData.wizardData);
				setWizardData(bookingAsWizardData.wizardData);
				fetchTripsForWizard(
					bookingAsWizardData.wizardData.departureTicketId ?? '',
					bookingAsWizardData.wizardData.returningTicketId ?? '',
					selectedTrips,
					(trips: SelectedTrips) => {
						setSelectedTrips(trips);
					},
					wizardData.userId,
				);
				navigate('/booking-wizard/vehicle');
			});
		}
	}, [wizardData.bookingToEdit, wizardData.userId, isJourneyElementsLoading]);

	// Booking for someone else is BookOnBehalfOfCustomer; without it the booking is the signed-in user's own.
	const canBookOnBehalf = store.can('BookOnBehalfOfCustomer');
	if (store.loggedIn && !canBookOnBehalf && wizardData.userId === '') {
		const newData = new BookingWizardData({ ...wizardData });
		newData.userId = store.userId ?? '';
		if (newData.innerWizardData instanceof VehicleBookingWizardData) {
			newData.innerWizardData.driverFirstName = store.userData?.firstName ?? '';
			newData.innerWizardData.driverLastName = store.userData?.lastName ?? '';
			newData.innerWizardData.driverPhone = store.userData?.phone ?? '';
			assignDriverDetailsToPassenger(newData);
		}
		setWizardData(newData);
	}

	const tabs: BookingWizardTab[] = [
		{
			name: BookingWizardTabNames.Search,
			displayName: 'Search',
			path: 'search',
			className: 'search-step',
			tabTitle: 'Search trip',
			visibility: isMobileBookingWizard ? VisibilityStatus.VISIBLE : VisibilityStatus.EXCLUDED,
			sidebarStatus: SidebarStatus.NO_SIDEBAR,
			validateStep: updateErrorState => {
				const newBaseErrors: WizardErrors<BookingWizardData> = {};
				const newInnerErrors: WizardErrors<PassengerBookingWizardData | VehicleBookingWizardData> = {};

				if (
					canNavigateToCartStep(location) ||
					canNavigateToPaymentStep(location) ||
					canNavigateToPostPaymentStep(location)
				) {
					if (updateErrorState) {
						setBaseWizardErrors(newBaseErrors);
						setInnerWizardErrors(newInnerErrors);
					}
					return true;
				}

				const { hasDriver, requiresDriver, hasPassengers, requiresPassengers } =
					checkPassengersAndDriver(wizardData);

				if (requiresPassengers && !hasPassengers) {
					newInnerErrors.adultTickets = 'Minimum of 1 passenger required';
				}
				if (!hasDriver && requiresDriver) {
					newInnerErrors.adultTickets = 'Driver required';
				}

				if (updateErrorState) {
					setInnerWizardErrors(newInnerErrors);
				}
				return Object.keys(newBaseErrors).length === 0 && Object.keys(newInnerErrors).length === 0;
			},
			component: <FerryFilters />,
			authorize: false,
			showTAndCsButton: false,
			isLinkDisabled: getDisableLinkForMultiFreight(wizardData),
		},
		...getBulkTicketsSteps(wizardData),
		{
			name: wizardData.tabSelected === 'freight' ? BookingWizardTabNames.Date : BookingWizardTabNames.Tickets,
			displayName: wizardData.tabSelected === 'freight' ? 'Date' : 'Tickets',
			path: 'tickets',
			tabTitle: wizardData.tabSelected === 'freight' ? 'Date' : 'Tickets',
			tabTitleEdit: wizardData.tabSelected === 'freight' ? 'Edit date' : 'Edit tickets',
			visibility: wizardData.isBulkBookingFlow ? VisibilityStatus.EXCLUDED : VisibilityStatus.VISIBLE,
			sidebarStatus: SidebarStatus.FILTERS,
			validateStep: updateErrorState => {
				// Tickets is skipped when running the wizard in bulk booking mode. This means that we don't need to do
				// validation for this step
				if (wizardData.isBulkBookingFlow) {
					return true;
				}

				const newBaseErrors: WizardErrors<BookingWizardData> = {};
				const newInnerErrors: WizardErrors<PassengerBookingWizardData | VehicleBookingWizardData> = {};

				if (
					canNavigateToCartStep(location) ||
					canNavigateToPaymentStep(location) ||
					canNavigateToPostPaymentStep(location)
				) {
					if (updateErrorState) {
						setBaseWizardErrors(newBaseErrors);
					}
					return true;
				}

				// One-way ferry trip validation. The error is surfaced on-screen in the trip-selection
				// section (see DepartureTripSection / ReturnTripSection) rather than via a toast.
				if (!wizardData.departingJourney) {
					newBaseErrors.departureTicketId = 'Please select a ferry trip to continue';
				}

				// Return ferry trip validation
				if (wizardData.returningTicketId === '' && wizardData.tripType === 'return') {
					newBaseErrors.returningTicketId = 'Please select a ferry trip to continue';
				}

				const { hasDriver, requiresDriver, hasPassengers, requiresPassengers } =
					checkPassengersAndDriver(wizardData);

				if (requiresPassengers && !hasPassengers) {
					newInnerErrors.adultTickets = 'Minimum of 1 passenger required';
				}
				if (!hasDriver && requiresDriver) {
					newInnerErrors.adultTickets = 'Driver required';
				}

				if (updateErrorState) {
					setBaseWizardErrors(newBaseErrors);
					setInnerWizardErrors(newInnerErrors);
				}

				return Object.keys(newBaseErrors).length === 0 && Object.keys(newInnerErrors).length === 0;
			},
			component: <TicketsTab selectedTrips={selectedTrips} />,
			authorize: false,
			showTAndCsButton: false,
			isLinkDisabled: getDisableLinkForMultiFreight(wizardData),
		},
		{
			name: 'Login',
			displayName: 'Login',
			tabTitle: 'Login',
			path: 'login',
			visibility: store.isVisitor ? VisibilityStatus.VISIBLE : VisibilityStatus.EXCLUDED,
			sidebarStatus: SidebarStatus.NO_SIDEBAR,
			validateStep: updateErrorState => {
				return true;
			},
			component: <LoginStep />,
			authorize: false,
			className: 'secondary-bg center-contents',
			contentClass: '',
			showTAndCsButton: false,
			hideBottomBar: true,
		},
		{
			name: BookingWizardTabNames.Customer,
			displayName: 'Customer',
			path: 'customer',
			tabTitle: 'Customer details',
			tabTitleEdit: 'Edit customer details',
			visibility: !canBookOnBehalf ? VisibilityStatus.EXCLUDED : VisibilityStatus.VISIBLE,
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: updateErrorState => {
				const newErrors: WizardErrors<BookingWizardData> = {};
				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					if (updateErrorState) {
						setBaseWizardErrors(newErrors);
					}
					return true;
				}

				if (stringIsEmpty(wizardData.userId)) {
					newErrors.userId = 'A customer must be selected';
				}

				if (updateErrorState) {
					setBaseWizardErrors(newErrors);
				}
				return Object.keys(newErrors).length === 0;
			},
			contentClass: wizardData.isBulkBookingFlow ? getCenteredPageContentClass() : undefined,
			component: <CustomerTab locationState={location.state as CustomLocationState} />,
			authorize: true,
			showTAndCsButton: false,
			isLinkDisabled: getDisableLinkForMultiFreight(wizardData),
		},
	];

	let bodyTabs = [];
	switch (wizardData.tabSelected) {
		case 'freight':
			bodyTabs = getFreightBookingTabs(
				wizardData,
				setBaseWizardErrors,
				setInnerWizardErrors,
				location,
				selectedTrips,
				freightTypeDtos,
				oldWizardData,
				subjectUserDisplayNameId,
			);
			break;
		case 'vehicle':
			bodyTabs = getVehicleBookingTabs(
				wizardData,
				setBaseWizardErrors,
				setInnerWizardErrors,
				selectedTrips,
				isStaff,
				location,
				navigate,
				oldWizardData,
				subjectUserDisplayNameId,
			);
			break;
		// Define other cases as needed
		default:
			bodyTabs = getPassengerBookingTabs(
				wizardData,
				setWizardData,
				setBaseWizardErrors,
				setInnerWizardErrors,
				selectedTrips,
				location,
				oldWizardData,
				subjectUserDisplayNameId,
			);
			break;
	}
	tabs.push(...bodyTabs);

	const resevationTabs: BookingWizardTab[] = [
		{
			name: BookingWizardTabNames.Reservation,
			displayName: 'Reservation',
			path: 'reservation',
			// this hidden step will be between vehicle and cart. We don't tell the user that this step exists so we
			// don't want a document title for it. Instead we will keep the same title from the previous step as it will
			// appear to the user that they are on the same page and waiting to progress to cart
			tabTitle: 'Vehicle details',
			tabTitleEdit: 'Edit vehicle details',
			visibility: VisibilityStatus.HIDDEN,
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: updateErrorState => {
				const newErrors: WizardErrors<BookingWizardData> = {};

				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					if (updateErrorState) {
						setBaseWizardErrors(newErrors);
						return true;
					}
				}

				// To get to cart step, there must exist a transactionId. If not, it means this reservation step has
				// been skipped. Returning false will then allow this step to be executed.
				return !bookingCreationFailed && getFerryBookingTransactionIdFromStorage() !== null;
			},
			contentClass: wizardData.isBulkBookingFlow ? getCenteredPageContentClass() : undefined,
			component: (
				<ReservationTab
					wizardData={wizardData}
					onUpdateWizardData={setWizardData}
					navigateToNextStep={() => {
						//
						// Important to use replace so that when the user clicks back on the browser, they will
						// be navigated to previous visible tab.
						//
						navigate(`${baseUrl}/cart`, { replace: true });
					}}
					navigateToPreviousStep={() => {
						const includedTabs = tabs.filter(x => x.visibility !== VisibilityStatus.EXCLUDED);
						const currentTabIndex = includedTabs.findIndex(x => x.path === 'reservation');
						const visibleTabs = includedTabs.filter(x => x.visibility === VisibilityStatus.VISIBLE);
						const previousTab =
							(currentTabIndex === -1 ? visibleTabs[0] : visibleTabs[currentTabIndex - 1])?.path ??
							'tickets';

						navigate(`${baseUrl}/${previousTab}`);
					}}
					navigateToTickets={() => {
						navigate(`${baseUrl}/tickets`);
					}}
					onCompleteBookingAttempt={setBookingCreationFailed}
				/>
			),
			authorize: true,
			showTAndCsButton: false,
			hideBottomBar: true,
		},
		{
			name: BookingWizardTabNames.Cart,
			displayName: 'Cart',
			path: 'cart',
			tabTitle: 'Cart',
			visibility: VisibilityStatus.VISIBLE,
			sidebarStatus: SidebarStatus.NO_SIDEBAR,
			validateStep: updateErrorState => {
				const newErrors: WizardErrors<BookingWizardData> = {};

				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					if (updateErrorState) {
						setBaseWizardErrors(newErrors);
					}
				}

				// If the user clicks through too fast then there can be problems related to the transaction id not
				// being present when the user tries to get the ts and cs. To solve this we can ensure that the user
				// can't progress unless the transaction id is set. This will only happen if the user is clicking
				// through the site really fast
				return getFerryBookingTransactionIdFromStorage() !== null;
			},
			component: (
				<CartTab
					wizardData={wizardData}
					selectedTrips={selectedTrips}
					onUpdateWizardData={setWizardData}
					setSelectedTrips={setSelectedTrips}
					setTermsAndConditionsTypes={setTermsAndConditionsTypes}
					isMobileBookingWizard={isMobileBookingWizard}
					setRefresh={setRefresh}
					refresh={refresh}
					setTabIsLoading={setTabIsLoading}
				/>
			),
			authorize: true,
			className: 'cart-step',
			showTAndCsButton: false,
		},
		{
			name: BookingWizardTabNames.TermsAndConditions,
			displayName: 'Ts-and-Cs',
			path: 't-and-cs',
			tabTitle: 'Terms and Conditions',
			visibility: VisibilityStatus.HIDDEN,
			sidebarStatus: SidebarStatus.NO_SIDEBAR,
			validateStep: updateErrorState => {
				const newErrors: WizardErrors<BookingWizardData> = {};

				if (!wizardData.acceptedTsAndCs) {
					newErrors.acceptedTsAndCs = 'Must accept terms and conditions to continue';
				}

				if (updateErrorState) {
					setBaseWizardErrors(newErrors);
				}
				return Object.keys(newErrors).length === 0;
			},
			component: (
				<TermsAndConditions
					acceptedTsandCs={acceptedTandCsStatic}
					isMobileBookingWizard={isMobileBookingWizard}
					termsAndConditionsTypes={termsAndConditionsTypes ?? []}
					isAlteration={wizardData.wizardMode === 'ALTERATION'}
					bookingToEditId={wizardData.bookingToEdit}
					redirectOnAccepted={ROUTES.BOOKING_WIZARD.payment}
					userTypeTerms={userTypeTerms}
				/>
			),
			authorize: true,
			className: 'cart-step',
			showTAndCsButton: true,
		},
	];
	tabs.push(...resevationTabs);

	// const extraTermsAndConditionTabs = useTermsAndConditionTabs();
	// tabs.push(...extraTermsAndConditionTabs);

	const paymentTabs: BookingWizardTab[] = [
		{
			name: 'Payment',
			displayName: 'Payment',
			path: 'payment',
			tabTitle: 'Payment',
			className: 'payment-step',
			visibility: VisibilityStatus.VISIBLE,
			sidebarStatus: SidebarStatus.NO_SIDEBAR,
			validateStep: updateErrorState => {
				if (updateErrorState) {
					setBaseWizardErrors({});
				}
				return true;
			},
			component: (
				<PaymentTabWrap
					timeoutExpiryUrl={`${baseUrl}/cart`}
					paymentCompletedUrl={`${baseUrl}/post-payment`}
					nameToPrefill={
						store.userData !== undefined && !isStaff
							? `${store.userData.firstName} ${store.userData.lastName}`
							: ''
					}
					onCompletePayment={() => {
						setPaymentDetailsEntered(true);
					}}
					onPageLoaded={() => {
						setPageLoaded(true);
					}}
					returnTrip={wizardData.tripType === 'return'}
					userId={wizardData.userId}
					isAlteration={wizardData.wizardMode === 'ALTERATION'}
					tripType={wizardData.tripType}
					bookingToEditId={wizardData.bookingToEdit}
					eventBooking={false}
				/>
			),
			authorize: true,
			showTAndCsButton: false,
			//
			// When on the PaymentTab, we want to use the continue button to trigger the
			// payment form. We do this by setting the formId of the button and set type
			// to 'submit'.
			//
			continueBtnProps: {
				form: PAYMENT_FORM_ID,
				type: 'submit',
				disabled: disableContinueButton || tabIsLoading,
			},
			continueBtnText: 'Pay now',
			continueBtnLoadingText: 'Processing...',
			hideBottomBar: !pageLoaded || paymentProcessing,
		},
		{
			name: BookingWizardTabNames.PostPayment,
			displayName: 'Post Payment',
			path: 'post-payment',
			tabTitle: 'Payment',
			visibility: VisibilityStatus.HIDDEN,
			sidebarStatus: SidebarStatus.NO_SIDEBAR,
			validateStep: updateErrorState => {
				if (updateErrorState) {
					setBaseWizardErrors({});
				}
				return true;
			},
			component: (
				<PostPaymentTab
					onSuccess={(transactionId: string) => {
						onWizardSuccess(
							navigate,
							transactionId,
							wizardData.wizardMode === 'ALTERATION',
							wizardData.returningTicketId !== '',
							false,
							wizardData.userId,
						);
					}}
					onPaymentFailed={errorCode => {
						navigate(`${baseUrl}/payment?ErrorCode=${errorCode}`);
					}}
				/>
			),
			authorize: true,
			showTAndCsButton: false,
			contentClass: 'post-payment-step-content',
			hideBottomBar: true,
		},
	];

	tabs.push(...paymentTabs);

	return (
		<BookingWizard
			selectedTrips={selectedTrips}
			tabs={tabs}
			baseUrl="/booking-wizard"
			isMobileBookingWizard={isMobileBookingWizard}
			disableBackAndContinueButtons={paymentDetailsEntered}
			setDisableContinueButton={setDisableContinueButton}
			disableContinueButton={disableContinueButton}
			tabIsLoading={tabIsLoading}
			paymentProcessing={paymentProcessing}
		/>
	);
}
